import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from './components/Layout/Header.jsx';
import Sidebar from './components/Layout/Sidebar.jsx';
import Settings from './components/Settings/Settings.jsx';
import ProjectsView from './components/Projects/ProjectsView.jsx';
import ProjectDetailView from './components/Projects/ProjectDetailView.jsx';
import OverviewView from './components/Overview/OverviewView.jsx';
import Login from './components/Auth/Login.jsx';
import NoCompanyAccess from './components/Auth/NoCompanyAccess.jsx';
import { useConversation, ConversationProvider } from './hooks/useConversation.js';
import { AuthProvider, useAuth } from './contexts/AuthContext.jsx';
import { trackUserEvent } from './services/api.js';
import './App.css';
import DashboardView from './components/Dashboard/DashboardView.jsx';
import UpdateBanner from './components/UpdateBanner/UpdateBanner.jsx';
import CompanyQuerySync from './routing/CompanyQuerySync.jsx';
import ConversationQuerySync from './routing/ConversationQuerySync.jsx';
import { inboundSearch } from './routing/navigation.js';

const VIEW_EVENTS = {
  overview: { event: 'view_future_cashflow', path: '/overview' },
  projects: { event: 'view_projects_overview', path: '/projects' },
  dashboards: { event: 'view_dashboard', path: '/dashboards' }
};

function viewFromPath(pathname) {
  if (pathname === '/overview') return 'overview';
  if (pathname === '/projects') return 'projects';
  if (pathname === '/dashboards') return 'dashboards';
  return null;
}

function RedirectWithParams({ to }) {
  const [searchParams] = useSearchParams();
  return <Navigate to={`${to}${inboundSearch(searchParams)}`} replace />;
}

function AppShell({ children }) {
  return (
    <div className="App">
      <Header />
      <div className="app-layout">
        <Sidebar />
        <main className="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}

function MainApp() {
  const { t } = useTranslation();
  const { activeCompany } = useAuth();
  const location = useLocation();
  const activeView = viewFromPath(location.pathname);
  const { error } = useConversation();

  useEffect(() => {
    if (!activeCompany?.id || !activeView) return;

    const LAST_VIEW_KEY = `revetall-analytics-last-view|${activeCompany.id}`;
    const mapped = VIEW_EVENTS[activeView];
    if (!mapped) return;

    try {
      const last = sessionStorage.getItem(LAST_VIEW_KEY);
      if (last === activeView) return;
      sessionStorage.setItem(LAST_VIEW_KEY, activeView);
    } catch {
      // ignore storage errors
    }

    trackUserEvent({
      companyId: activeCompany.id,
      event: mapped.event,
      operation: 'read',
      path: mapped.path
    });
  }, [activeView, activeCompany?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const HIDDEN_AT_KEY = 'revetall-analytics-hidden-at';
    const THRESHOLD_MS = 10 * 60 * 1000;

    const onVisibilityChange = () => {
      try {
        if (document.visibilityState === 'hidden') {
          sessionStorage.setItem(HIDDEN_AT_KEY, String(Date.now()));
          return;
        }

        if (document.visibilityState !== 'visible') return;
        const hiddenAtRaw = sessionStorage.getItem(HIDDEN_AT_KEY);
        if (!hiddenAtRaw) return;
        sessionStorage.removeItem(HIDDEN_AT_KEY);

        const hiddenAt = Number(hiddenAtRaw);
        if (!Number.isFinite(hiddenAt)) return;
        const hiddenDuration = Date.now() - hiddenAt;
        if (hiddenDuration < THRESHOLD_MS) return;

        if (!activeCompany?.id || !activeView) return;
        const mapped = VIEW_EVENTS[activeView];
        if (!mapped) return;

        try {
          const LAST_VIEW_KEY = `revetall-analytics-last-view|${activeCompany.id}`;
          sessionStorage.setItem(LAST_VIEW_KEY, activeView);
        } catch {
          // ignore
        }

        trackUserEvent({
          companyId: activeCompany.id,
          event: mapped.event,
          operation: 'read',
          path: mapped.path
        });
      } catch {
        // swallow
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [activeView, activeCompany?.id]);

  return (
    <div className="App">
      <ConversationQuerySync />
      <Header />

      <div className="app-layout">
        <Sidebar />

        <main className="main-content">
          <Routes>
            <Route path="/" element={<RedirectWithParams to="/overview" />} />
            <Route path="/overview" element={<OverviewView />} />
            <Route path="/projects" element={<ProjectsView />} />
            <Route path="/projects/:projectId" element={<ProjectDetailView />} />
            <Route path="/dashboards" element={<DashboardView />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<RedirectWithParams to="/overview" />} />
          </Routes>
        </main>
      </div>

      {error && (
        <div className="error-banner">
          <span>{t('app.error')}: {error}</span>
        </div>
      )}
    </div>
  );
}

function AppContent() {
  const { t } = useTranslation();
  const { user, loading, hasCompanyAccess, companyError } = useAuth();

  if (loading) {
    return (
      <AppShell>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">{t('app.loading')}</p>
        </div>
      </AppShell>
    );
  }

  if (!user) {
    return <Login />;
  }

  if (!hasCompanyAccess) {
    return (
      <AppShell>
        <NoCompanyAccess companyError={companyError} />
      </AppShell>
    );
  }

  return (
    <ConversationProvider>
      <MainApp />
    </ConversationProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <UpdateBanner />
      <CompanyQuerySync />
      <AppContent />
    </AuthProvider>
  );
}

export default App;
